import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const CompanySettingsContext = createContext();

export const useCompanySettings = () => {
  const context = useContext(CompanySettingsContext);
  if (!context) {
    throw new Error('useCompanySettings must be used within a CompanySettingsProvider');
  }
  return context;
};

export const CompanySettingsProvider = ({ children }) => {
  const [companySettings, setCompanySettings] = useState({
    companyName: 'STOCKPRO INVENTORY',
    companyNameArabic: 'ستوك برو انفانتري',
    description: 'Système de Gestion d\'Inventaire',
    descriptionSubtitle: 'Solutions logicielles d\'entreprise',
    address: 'Alger Centre, Algérie',
    postalCode: '16000',
    phone: '+213 (0) 21 XX XX XX',
    email: 'contact@stockpro.dz',
    website: 'www.stockpro.dz',
    
    // Legal information
    articleNumber: '00/00-000123456-B12',
    nifNumber: '000123456789',
    nisNumber: '000123456789',
    rcNumber: '98/00-0123456-B21',
    
    // Banking information
    bankAccount1: '1234567890123456 CPA ALGER',
    bankAccount2: '9876543210987654 BADR ALGER',
    
    // Document settings
    defaultTaxRate: 19,
    currency: 'DA'
  });
  
  const [isLoading, setIsLoading] = useState(true);

  // Load company settings from localStorage first, then try API
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        // First try to load from localStorage
        const savedSettings = localStorage.getItem('stockpro_company_settings');
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          setCompanySettings(parsedSettings);
          console.log('Loaded company settings from localStorage');
        }

        // Then try to get from API (will update if available)
        try {
          const response = await axios.get('/api/settings/company');
          if (response.data) {
            // Use the response data directly since the backend now provides all company fields with defaults
            setCompanySettings(response.data);
            // Also save to localStorage
            localStorage.setItem('stockpro_company_settings', JSON.stringify(response.data));
            console.log('Updated company settings from API');
          }
        } catch (apiError) {
          console.log('Could not fetch settings from API, using localStorage or defaults');
          // If we already loaded from localStorage, that's fine
          // If not, we'll use the default values initialized in state
        }
      } catch (error) {
        console.error('Error handling company settings:', error);
        // Use default values if both localStorage and API fail
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanySettings();
  }, []);

  // Update company settings through API with localStorage fallback
  const updateCompanySettings = async (newSettings) => {
    try {
      // Update state immediately
      const updatedSettings = { ...companySettings, ...newSettings };
      setCompanySettings(updatedSettings);
      
      // Always save to localStorage first for immediate persistence
      localStorage.setItem('stockpro_company_settings', JSON.stringify(updatedSettings));
      
      // Clean the data before sending to API
      const cleanedSettings = {};
      Object.keys(newSettings).forEach(key => {
        const value = newSettings[key];
        if (value !== undefined && value !== null && value !== '') {
          cleanedSettings[key] = value;
        } else if (['email', 'website', 'companyNameArabic', 'postalCode'].includes(key)) {
          // For optional fields that can be empty, send null instead of empty string
          cleanedSettings[key] = null;
        } else if (value === '') {
          // For required-ish fields, keep the empty string
          cleanedSettings[key] = value;
        }
      });
      
      // Try to save to backend
      try {
        await axios.put('/api/settings/company', cleanedSettings);
        toast.success('Paramètres de l\'entreprise mis à jour');
      } catch (apiError) {
        console.warn('Could not save settings to API, but saved to localStorage:', apiError);
        // Show a different message when in deployed environment
        if (window.location.hostname.includes('pages.dev') || 
            window.location.hostname !== 'localhost') {
          toast.success('Paramètres sauvegardés localement');
        } else {
          // Show more specific error message for localhost/development
          if (apiError.response?.status === 413) {
            toast.error('Le fichier est trop volumineux. Veuillez choisir une image plus petite.');
          } else if (apiError.response?.data?.details) {
            const errorDetails = Array.isArray(apiError.response.data.details) 
              ? apiError.response.data.details.join(', ')
              : apiError.response.data.details;
            toast.error(`Erreur de validation: ${errorDetails}`);
          } else if (apiError.response?.data?.error) {
            toast.error(`Erreur: ${apiError.response.data.error}`);
          } else if (apiError.code === 'NETWORK_ERROR' || !apiError.response) {
            toast.error('Erreur de connexion au serveur');
          } else {
            toast.error('Erreur lors de la mise à jour des paramètres sur le serveur');
          }
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error updating company settings:', error);
      toast.error('Erreur lors de la mise à jour des paramètres');
      return false;
    }
  };

  const value = {
    companySettings,
    updateCompanySettings,
    isLoading
  };

  return (
    <CompanySettingsContext.Provider value={value}>
      {children}
    </CompanySettingsContext.Provider>
  );
};